import React, {useId, useMemo} from 'react'
import {Box, useRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import moduleContainerRecipe from './index.recipe'
import {sanitizeAnchorId} from '../../../utils/utils'
import {HeadingProvider} from '../heading/context'

/**
 * A container component for CMS modules that:
 * - wraps content in a section element
 * - adds an optional anchor ID
 * - applies background color
 * - applies page container margins
 *
 * @component
 * @params {Object} props - Component properties.
 * @param {string} [props.anchor] - Optional ID for the section element to enable anchor linking.
 * @param {string} [props.background] - Background style for the container. One of 'default', 'subtle', 'strong', or 'inverse'.
 * @param {React.ReactNode} props.children - The content to be wrapped inside the section.
 * @returns {JSX.Element} A section element containing the provided content.
 */
const ModuleContainer = React.forwardRef(
    (
        {
            anchor,
            background = 'default',
            css,
            moduleId,
            moduleType,
            pageId,
            spacing,
            layerStyle = 'container.page',
            modulePadding = 'default',
            'aria-label': legacyAriaLabel,
            ...props
        },
        ref
    ) => {
        if (process.env.NODE_ENV !== 'production' && legacyAriaLabel) {
            throw new Error(
                `ModuleContainer (moduleType: ${moduleType}): Do not pass "aria-label". ` +
                    `This component now uses "aria-labelledby" to link the section to the internal Heading component. ` +
                    `If the heading should be invisible, use variant="hidden" on the Heading component instead.`
            )
        }

        const recipe = useRecipe({recipe: moduleContainerRecipe})
        const styles = useMemo(
            () => recipe({background, spacing, modulePadding}),
            [recipe, background, spacing, modulePadding]
        )
        const sanitizedAnchor = useMemo(() => sanitizeAnchorId(anchor), [anchor])

        const headingId = useId()

        return (
            <HeadingProvider value={{headingId}}>
                <Box css={styles} ref={ref}>
                    <Box
                        {...props}
                        as="section"
                        css={{
                            ...css
                        }}
                        id={sanitizedAnchor}
                        layerStyle={layerStyle}
                        aria-labelledby={headingId}
                        data-module-id={moduleId}
                        data-module-type={moduleType}
                        data-page-id={pageId}
                    />
                </Box>
            </HeadingProvider>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    ModuleContainer.displayName = 'ModuleContainer'
}

ModuleContainer.propTypes = {
    'aria-label': PropTypes.string,
    anchor: PropTypes.string,
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    css: PropTypes.object,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    spacing: PropTypes.string,
    layerStyle: PropTypes.string,
    modulePadding: PropTypes.string,
    ariaLabel: PropTypes.string
}

export default ModuleContainer
